import { useReactTable, getExpandedRowModel, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { isActionColumn } from './ActionColumn.js';
import { makeColumnDef, isDataColumn } from './Column.js';
import { isContent } from './Content.js';
import { TableRow } from './Row.js';
import styles from './Table.module.scss.js';
import { arrToState, xor, stateToArr } from './util.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Text } from '../text/Text.js';
import classNames from 'clsx';
import useResizeObserver from 'use-resize-observer';

function Table(props) {
  const {
    rows: data,
    columns: callerCols,
    expandableRows,
    expandableContent,
    hasMore,
    outline = 'none',
    size = 'default',
    className: callerClassName,
    selectionMode = 'none',
    zebra,
    highlightOnHover,
    disableExpand,
    disableAction,
    disableSelect,
    onSelect,
    expanded,
    onExpand,
    rowKey,
    sorting,
    selected = [],
    frozenRows = [],
    tag,
    rowColor
  } = props;
  const rowSelection = useMemo(() => arrToState(selected), [selected]);
  const expandedState = useMemo(() => arrToState(expanded), [expanded]);
  const columns = useMemo(() => callerCols.map(makeColumnDef), [callerCols]);
  const frozenCols = useMemo(() => callerCols.filter(x => isDataColumn(x) && x.frozen).map(x => x.key), [callerCols]);
  const anyExpandable = useMemo(() => !!(hasMore && data.find(x => !isContent(x) && hasMore(x))), [data, hasMore]);
  const anySelectable = selectionMode !== 'none';
  const anyActionable = useMemo(() => !!callerCols.find(isActionColumn), [callerCols]);
  const anyFrozen = frozenCols.length > 0 || frozenRows.length > 0;
  const canFocusRows = xor(anyExpandable, anySelectable, anyActionable) && !anyFrozen;
  const meta = {
    anyExpandable,
    anySelectable,
    canFocusRows,
    sorting,
    disableExpand,
    disableAction,
    disableSelect,
    selectionMode,
    rowColor,
    size
  };
  const getRowId = rowKey ? o => isContent(o) ? o.rowId : rowKey(o).toString() : undefined;
  const handleRowSelectionChange = useCallback(u => {
    const next = typeof u === 'function' ? u(rowSelection) : u;

    // TanStack Table bug: select all doesn't respect canSelect.
    // https://github.com/TanStack/table/issues/5398
    // after the update has been evaluated, reset each disabled row back to what it originally was.
    const disabledRows = table.getRowModel().flatRows.filter(x => disableSelect?.(x.original));
    for (const r of disabledRows) {
      next[r.id] = rowSelection[r.id];
    }

    // unchecking children does not uncheck the parent. this might be working as the library intends.
    // https://github.com/TanStack/table/issues/5620
    const roots = table.getRowModel().rows;
    function setSelection(row) {
      const dataRows = row.subRows.filter(x => !isContent(x.original));
      if (dataRows.length === 0) {
        return;
      }
      for (const s of dataRows) {
        setSelection(s);
      }
      const all = dataRows.every(x => next[x.id] === true);
      next[row.id] = all;
    }
    for (const r of roots) {
      setSelection(r);
    }
    onSelect?.(stateToArr(next));
  },
  // TODO: table is not included, but including it is circular.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [disableSelect, onSelect, rowSelection]);
  const handleRowExpandChange = useCallback(u => {
    const next = typeof u === 'function' ? u(expandedState) : u;
    if (typeof next === 'boolean') {
      throw new Error('this should not happen');
    }
    onExpand?.(stateToArr(next));
  }, [expandedState, onExpand]);
  const enableRowSelection = selectionMode === 'multi' ? x => {
    if (isContent(x.original)) {
      return false;
    }
    if (!disableSelect) {
      return true;
    }
    return !disableSelect(x.original);
  } : false;
  const table = useReactTable({
    data,
    columns: columns,
    meta,
    state: {
      expanded: expandedState,
      rowSelection,
      columnPinning: {
        left: frozenCols
      },
      rowPinning: {
        top: frozenRows
      }
    },
    enableRowSelection,
    enableColumnPinning: true,
    enableRowPinning: true,
    manualSorting: true,
    getRowId,
    onRowSelectionChange: handleRowSelectionChange,
    onExpandedChange: handleRowExpandChange,
    getRowCanExpand: x => !!hasMore?.(x.original),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: x => {
      if (isContent(x)) {
        return;
      }
      const content = expandableContent?.(x);
      const rows = expandableRows?.(x);
      const rowId = rowKey(x) + '_content';
      const subrows = [];
      if (content) {
        subrows.push({
          __isContent: true,
          rowId,
          content
        });
      }
      if (rows) {
        subrows.push(...rows);
      }
      return subrows;
    }
  });
  const tableClassName = classNames(styles['table'], styles[`outline-${outline}`], styles[`size-${size}`], zebra && styles['zebra'], (canFocusRows || highlightOnHover) && styles['highlight-on-hover']);
  const centerTableRef = useRef(null);
  const topTableRef = useRef(null);
  const leftTableRef = useRef(null);
  const setTableSizes = useCallback(() => {
    const ct = centerTableRef.current;
    const tt = topTableRef.current;
    const lt = leftTableRef.current;
    if (frozenCols.length > 0 && lt && ct) {
      // Make row heights equal between left and center tables
      const centerTrs = [...ct.querySelectorAll('tr')];
      const leftTrs = [...lt.querySelectorAll('tr')];
      const tableHeight = Math.max(lt.getBoundingClientRect().height, ct.getBoundingClientRect().height);
      lt.style.height = `${tableHeight}px`;
      ct.style.height = `${tableHeight}px`;
      for (let i = 0; i < centerTrs.length; i++) {
        const ctr = centerTrs[i];
        const ltr = leftTrs[i];
        const trHeight = Math.max(ltr.getBoundingClientRect().height, ctr.getBoundingClientRect().height);
        ltr.style.height = `${trHeight}px`;
        ctr.style.height = `${trHeight}px`;
      }
      return;
    }
    if (frozenRows.length > 0 && tt && ct) {
      // Apply top widths to center table
      const topTds = [...tt.querySelectorAll('tr:last-child td')];
      const centerTds = [...ct.querySelectorAll('tr:first-child td')];
      ct.style.width = tt.getBoundingClientRect().width + 'px';
      for (let i = 0; i < topTds.length; i++) {
        const ctd = centerTds[i];
        const ttd = topTds[i];
        ctd.style.width = window.getComputedStyle(ttd).width;
      }
    }
  }, [frozenCols.length, frozenRows.length]);
  useResizeObserver({
    ref: topTableRef,
    onResize: setTableSizes
  });
  useResizeObserver({
    ref: leftTableRef,
    onResize: setTableSizes
  });
  useLayoutEffect(setTableSizes, [setTableSizes]);
  if (anyFrozen && anyExpandable) {
    return /*#__PURE__*/jsx(Text, {
      variant: "code",
      color: "color-danger",
      children: "Frozen rows and columns cannot be used with expandable content."
    });
  }
  const headerGroups = table.getCenterHeaderGroups().map(hg => /*#__PURE__*/jsx("tr", {
    children: hg.headers.map(h => /*#__PURE__*/jsx("th", {
      colSpan: h.colSpan,
      "data-dsx--table--heading": h.id,
      children: flexRender(h.column.columnDef.header, h.getContext())
    }, h.id))
  }, hg.id));
  const rows = table.getCenterRows().map(r => /*#__PURE__*/jsx(TableRow, {
    row: r,
    tableMeta: meta,
    side: "center"
  }, r.id));
  if (frozenRows.length > 0) {
    const topRows = table.getTopRows().map(r => /*#__PURE__*/jsx(TableRow, {
      row: r,
      tableMeta: meta,
      side: "center"
    }, r.id));
    return /*#__PURE__*/jsxs("div", {
      className: classNames(callerClassName, styles['col']),
      children: [/*#__PURE__*/jsxs("table", {
        "data-dsx--table--top": tag,
        className: classNames(tableClassName, styles['frozen-row-table']),
        ref: topTableRef,
        children: [/*#__PURE__*/jsx("thead", {
          children: headerGroups
        }), /*#__PURE__*/jsx("tbody", {
          children: topRows
        })]
      }), /*#__PURE__*/jsx("div", {
        className: styles['scroll-y'],
        children: /*#__PURE__*/jsx("table", {
          "data-dsx--table--center": tag,
          className: classNames(tableClassName, styles['scroll-y']),
          ref: centerTableRef,
          children: /*#__PURE__*/jsx("tbody", {
            children: rows
          })
        })
      })]
    });
  }
  if (frozenCols.length > 0) {
    const leftHeaderGroups = table.getLeftHeaderGroups().map(hg => /*#__PURE__*/jsx("tr", {
      children: hg.headers.map(h => /*#__PURE__*/jsx("th", {
        colSpan: h.colSpan,
        "data-dsx--table--heading": h.id,
        children: flexRender(h.column.columnDef.header, h.getContext())
      }, h.id))
    }, hg.id));
    const leftRows = table.getRowModel().rows.map(r => /*#__PURE__*/jsx(TableRow, {
      row: r,
      tableMeta: meta,
      side: "left"
    }, r.id));
    return /*#__PURE__*/jsxs("div", {
      className: classNames(callerClassName, styles['row']),
      children: [/*#__PURE__*/jsxs("table", {
        "data-dsx--table--left": tag,
        className: classNames(tableClassName, styles['frozen-col-table']),
        ref: leftTableRef,
        children: [/*#__PURE__*/jsx("thead", {
          children: leftHeaderGroups
        }), /*#__PURE__*/jsx("tbody", {
          children: leftRows
        })]
      }), /*#__PURE__*/jsx("div", {
        className: styles['scroll-x'],
        children: /*#__PURE__*/jsxs("table", {
          "data-dsx--table--center": tag,
          className: tableClassName,
          ref: centerTableRef,
          children: [/*#__PURE__*/jsx("thead", {
            children: headerGroups
          }), /*#__PURE__*/jsx("tbody", {
            children: rows
          })]
        })
      })]
    });
  }
  return /*#__PURE__*/jsxs("table", {
    "data-dsx--table": tag,
    className: classNames(callerClassName, tableClassName),
    ref: centerTableRef,
    children: [/*#__PURE__*/jsx("thead", {
      children: headerGroups
    }), /*#__PURE__*/jsx("tbody", {
      children: rows
    })]
  });
}

export { Table };
